import { HStack, Text, VStack, Box } from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import {
  CallUnScreenIcon,
  CallScreenIcon,
  CallMuteIcon,
  CallUnMuteIcon,
  CallUnSpeakerIcon,
  CallSpeakerIcon,
  CallVideoIcon,
  CallUnVideoIcon,
  CallVideoHangUpIcon,
  CallAddMembersIcon,
  CallMoreIcon,
  CallCloseIcon,
  CallChatIcon,
  CallVoiceJoinIcon,
  CallDownIcon,
  CallZoomOutIcon,
  CallNoDeviceIcon,
  CallExpendIcon,
  CallUnExpendIcon,
  Tooltip,
  toaster,
} from '@halo/ui-chat-react';
import { CallStatus, PermissionName, PermissionStatusState } from '@halo/chat-sdk-react';
import { useTranslation } from 'react-i18next';
import { CardMoreOperateKey } from '../CallMaxWindowPanel/type';
import MoreOperate from './MoreOperate';
import CallTimer from './CallTimer';
import VideoOperate from './VideoOperate';
import AudioOperate from './AudioOperate';
import SpeakerOperate from './SpeakerOperate';
// import ScreenOperate from './ScreenOperate';

export enum DeviceStatus {
  DISABLED,
  ENABLED,
  UNPUBLISHED,
  ERROR,
}

export interface CallOperateProps {
  name?: string;
  muteState?: boolean;
  speakerState?: boolean;
  videoState?: boolean;
  screenState?: boolean;
  hangup?: boolean;
  devices?: Record<
    'audioinput' | 'audiooutput' | 'videoinput',
    {
      list: Array<{ key: string; label: React.ReactNode; disabled?: boolean }>;
      activatedId: string;
      status: DeviceStatus;
    }
  >;
  startTime?: number;
  container?: React.RefObject<HTMLDivElement | null>;
  mediaIsReady?: boolean;
  callStatus?: CallStatus;
  devicessPermission?: { permissionStatus: PermissionStatus; permissionName: PermissionName }[];
  isSingleSession?: boolean;
  setZoom: (zoom: boolean) => void;
  onHangup?: () => void;
  onDeviceChange?: (
    deviceType: 'audioinput' | 'audiooutput' | 'videoinput',
    deviceId: string
  ) => void;
  onDeviceSwitch?: (
    deviceType: 'audioinput' | 'audiooutput' | 'videoinput' | 'screen'
  ) => Promise<void>;
  onAddMembers?: () => void;
}

export function CallMaxActions(props: CallOperateProps) {
  const { hangup = false, setZoom, name = '', startTime, mediaIsReady, callStatus } = props;
  const actionRef = useRef<HTMLDivElement>(null);
  return (
    <HStack h="74px" w="full" justify="space-between" alignItems="center">
      <CallFooterLeftInfo
        name={name}
        startTime={startTime}
        mediaIsReady={mediaIsReady}
        callStatus={callStatus}
        isSingleSession={props.isSingleSession}
      />
      <HStack gap={2.5} alignItems="center" ref={actionRef}>
        {!hangup ? <CallNormalActions {...props} container={actionRef} /> : <CallOtherHangUp />}
      </HStack>
      <CallFooterRightInfo setZoom={setZoom} />
    </HStack>
  );
}

export default CallMaxActions;

export function CallNormalActions(props: CallOperateProps) {
  const {
    muteState = true,
    speakerState = true,
    videoState = true,
    screenState = true,
    onHangup,
    devices,
    onDeviceChange,
    onDeviceSwitch,
    onAddMembers,
    container,
    devicessPermission,
    isSingleSession,
  } = props;
  const { t } = useTranslation();

  const hasPermission = useCallback(
    (name: PermissionName) => {
      return !!devicessPermission?.find(
        (permission) =>
          permission.permissionStatus.state === PermissionStatusState.DENIED &&
          permission.permissionName === name
      );
    },
    [devicessPermission]
  );

  const handleClickMore = useCallback((key: string) => {
    switch (key) {
      case CardMoreOperateKey.FULL_SCREEN:
        document.querySelector('#callMaxWindowPanel')?.requestFullscreen();
        break;
      default:
        break;
    }
  }, []);

  return (
    <>
      <ActionWithExpend
        expend={videoState}
        noDevice={!devices?.videoinput?.list?.length}
        menuComponent={(icon) => (
          <VideoOperate
            value={devices?.videoinput?.activatedId}
            onClick={(deviceId) => {
              onDeviceChange?.('videoinput', deviceId);
            }}
            items={devices?.videoinput?.list}
            container={container}
          >
            {icon}
          </VideoOperate>
        )}
        onClick={async () => {
          const unPermission = hasPermission(PermissionName.CAMERA);
          if (unPermission) {
            toaster.warning({ title: t('calls.noCameraPermission') });
            return;
          }
          await onDeviceSwitch?.('videoinput');
        }}
      >
        {videoState ? <CallVideoIcon /> : <CallUnVideoIcon />}
      </ActionWithExpend>
      <ActionWithExpend
        expend={speakerState}
        noDevice={!devices?.audioinput?.list?.length}
        menuComponent={(icon) => (
          <SpeakerOperate
            value={devices?.audioinput?.activatedId}
            onClick={(deviceId) => {
              onDeviceChange?.('audioinput', deviceId);
            }}
            items={devices?.audioinput?.list}
            container={container}
          >
            {icon}
          </SpeakerOperate>
        )}
        onClick={async () => {
          const unPermission = hasPermission(PermissionName.MICROPHONE);
          if (unPermission) {
            toaster.warning({ title: t('calls.noMicrophonePermission') });
            return;
          }
          await onDeviceSwitch?.('audioinput');
        }}
      >
        {speakerState ? <CallSpeakerIcon /> : <CallUnSpeakerIcon />}
      </ActionWithExpend>
      <ActionWithExpend
        disabled={true}
        expend={muteState}
        noDevice={!devices?.audiooutput?.list?.length}
        menuComponent={(icon) => (
          <AudioOperate
            value={devices?.audiooutput?.activatedId}
            onClick={(deviceId) => {
              onDeviceChange?.('audiooutput', deviceId);
            }}
            items={devices?.audiooutput?.list}
            container={container}
          >
            {icon}
          </AudioOperate>
        )}
        onClick={async () => {
          const unPermission = hasPermission(PermissionName.MICROPHONE);
          if (unPermission) {
            toaster.warning({ title: t('calls.noMicrophonePermission') });
            return;
          }
          await onDeviceSwitch?.('audiooutput');
        }}
      >
        {muteState ? <CallMuteIcon /> : <CallUnMuteIcon />}
      </ActionWithExpend>
      <ActionWithExpend onClick={onHangup}>
        <CallVideoHangUpIcon />
      </ActionWithExpend>
      {!isSingleSession && (
        <ActionWithExpend onClick={onAddMembers}>
          <CallAddMembersIcon />
        </ActionWithExpend>
      )}
      <ActionWithExpend
        // expend={screenState}
        // menuComponent={(props) => <ScreenOperate onClick={handleMenuClick} {...props} />}
        onClick={async () => {
          await onDeviceSwitch?.('screen');
        }}
      >
        {screenState ? <CallScreenIcon /> : <CallUnScreenIcon />}
      </ActionWithExpend>
      <ActionWithExpend>
        <MoreOperate onClick={handleClickMore} container={container}>
          <CallMoreIcon />
        </MoreOperate>
      </ActionWithExpend>
    </>
  );
}

export function ActionWithExpend({
  expend,
  noDevice = false,
  disabled = false,
  children,
  menuComponent,
  onClick,
}: {
  expend?: boolean;
  noDevice?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  menuComponent?: (expendIcon: React.ReactNode) => React.ReactNode;
  onClick?: () => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [clicked, setClicked] = useState(false);
  const expendIcon = useMemo(() => {
    if (expend === undefined) return null;

    return expend ? <CallExpendIcon /> : <CallUnExpendIcon />;
  }, [expend]);

  const renderExpendIcon = useMemo(() => {
    if (expend === undefined) return null;

    if (noDevice) {
      return (
        <Tooltip content={t('calls.noFoundDivider')} positioning={{ placement: 'top' }}>
          <CallNoDeviceIcon />
        </Tooltip>
      );
    }

    if (menuComponent) {
      return menuComponent(expendIcon);
    }

    return expendIcon;
  }, [expend, noDevice, menuComponent, expendIcon, t]);

  const handleClick = useCallback(async () => {
    if (clicked) return;
    try {
      setClicked((prev) => !prev);
      await onClick?.();
    } finally {
      setClicked(false);
    }
  }, [clicked, onClick]);

  return (
    <Box position="relative" display="inline-block" onClick={handleClick}>
      <Box
        opacity={clicked ? 0.7 : 1}
        cursor={disabled ? 'inherit' : 'pointer'}
        _active={{
          opacity: disabled ? 1 : 0.7,
        }}
      >
        {children}
      </Box>
      {renderExpendIcon && (
        <Box
          position="absolute"
          top={-1.5}
          right={-1.5}
          bg="#B3B3B3"
          borderRadius="full"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <Box
            display="flex"
            cursor="pointer"
            _active={{
              opacity: 0.7,
            }}
          >
            {renderExpendIcon}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export function CallOtherHangUp({ noAnswer = true }: { noAnswer?: boolean }) {
  return (
    <>
      <CallCloseIcon />
      <CallChatIcon />
      {noAnswer && <CallVoiceJoinIcon />}
    </>
  );
}

export function CallFooterLeftInfo({
  name = 'Sky',
  mediaIsReady = false,
  startTime,
  callStatus,
  isSingleSession,
}: {
  name: string;
  mediaIsReady?: boolean;
  startTime?: number;
  callStatus?: CallStatus;
  isSingleSession?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <VStack gap="1" w="180px" alignItems="flex-start">
      {isSingleSession && (
        <HStack gap="1" w="full">
          <Text fontSize="sm" color="white" fontWeight="seimibold" truncate>
            {name}
          </Text>
          <Box flexShrink={0}>
            <CallDownIcon />
          </Box>
        </HStack>
      )}

      <Box color="fg.subtle">
        {mediaIsReady ? (
          <CallTimer startTime={startTime} />
        ) : callStatus === CallStatus.RINGING ? (
          t('calls.statusRinging')
        ) : (
          t('calls.statusConnecting')
        )}
      </Box>
    </VStack>
  );
}
export function CallFooterRightInfo({ setZoom }: { setZoom: (zoom: boolean) => void }) {
  return (
    <HStack alignItems="center" w="180px" justifyContent="right">
      <CallZoomOutIcon onClick={() => setZoom(false)} cursor="pointer" />
    </HStack>
  );
}
